const pagings = {};
const DEFAULT_PAGING_LENGTH = 30;

class Paging {
  constructor() {
    this.defaultValue = [];
  }

  needContextualParams() { return false; }

  addData(data, newData) { return data ? data.concat(newData) : newData; }

  getPageData(/* data, page */) {}

  requestParams(/* page */) { throw new Error('Not implemented'); }

  clear() { return []; }

  toTable(data) { return data; }
}

class DataTablesPaging extends Paging {
  constructor() {
    super();
    this.draw = 1;
    this.initDraw = this.draw;
    this.encodedColumns = undefined;
    this.refColumns = undefined;
    this.noSearch = [
      encodeURI('search[value]='),
      encodeURI('search[regex]= false'),
    ].join('&');
    this.headers = {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
    };
    this.defaultValue = null;
  }

  needContextualParams() { return true; }

  getDataRows(data) {
    return (!data || !data.data) ? [] : data.data;
  }

  addData(data, newData, page) {
    let dataDraw;
    try {
      dataDraw = parseInt(newData.draw, 10);
    } catch (e) {
      console.error(e);
      return data;
    }

    if (page.clear
        || (this.initDraw === dataDraw)
        || !data
        || !data.data) {
        // TODO: with constant changing data in the backend, following properties
        // are always changing, trigerring some refresh issue with reat-virtualized table
        // || !data.data
        // || !data.data.length
        // || (data.filteredCount !== newData.recordsFiltered)
        // || (data.totalCount !== newData.recordsTotal)) {
      return {
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/Array
        // Using fill method because of "Node: this implies an array of arrayLength empty slots, not slots with actual undefined values"
        data: page.startIndex ? new Array(page.startIndex).fill(undefined).concat(newData.data) : newData.data,
        filteredCount: newData.recordsFiltered,
        totalCount: newData.recordsTotal,
      };
    }
    if (this.initDraw > dataDraw) {
      return data;
    }
    // TODO: with constant changing data in the backend, following properties
    // are always changing, trigerring some refresh issue with reat-virtualized table
    data.filteredCount = newData.recordsFiltered;
    data.totalCount = newData.recordsTotal;
    const rows = data.data;
    const newRows = newData.data;
    // We are adding new data to an existent array.
    // We want to keep the reference to data to not generate a complete redraw
    // and copy newData into data.
    let iDest = page.startIndex;
    let iSrc = 0;
    let nMax = Math.min(rows.length - iDest, newRows.length);
    // Copy into the current array if page.startIndex < data.length
    while (iSrc < nMax) {
      rows[iDest] = newRows[iSrc];
      iSrc += 1;
      iDest += 1;
    }
    // Add undefined values up to page.startIndex if page.startIndex > data.length
    while (page.startIndex > rows.length) {
      rows.push(undefined);
    }
    // Add newData objects at the end of data if (page.startIndex + newData.length > data.length)
    nMax = newRows.length;
    while (iSrc < nMax) {
      rows.push(newRows[iSrc]);
      iSrc += 1;
    }
    return data;
  }

  getPageData(data, page) {
    return data.data.slice(page.startIndex || 0, page.stopIndex ? (page.stopIndex + 1) : DEFAULT_PAGING_LENGTH);
  }

  toTable(data, page) {
    return {
      data: data.data,
      filteredCount: data.recordsFiltered,
      totalCount: data.recordsTotal,
      startIndex: page.startIndex,
      stopIndex: page.stopIndex,
    };
  }

  encodeColumns(columns) {
    const getData = (col) => {
      return !col.valueRenderer && col.name && (col.name.indexOf('.') < 0) && col.name || '';
    };
    return columns.map((col, i) => {
      const prefix = `columns[${i}]`;
      return [
        `${prefix}[data]=${getData(col)}`,
        `${prefix}[name]=`,
        `${prefix}[searchable]=${col.valueRenderer ? 'false' : 'true'}`,
        `${prefix}[orderable]=${col.canSort ? 'true' : 'false'}`,
        `${prefix}[search][value]=`,
        `${prefix}[search][regex]=false`,
      ].map(x => encodeURI(x)).join('&');
    }).join('&');
  }

  encodeServerQuery(query) {
    if (!query) return null;
    const comps = query.substring(1).split('&').map(comp => comp.split('='));
    return comps.map(comp => `selection[${comp[0]}]=${comp[1]}`).join('&');
  }

  requestParams(page, ctx) {
    const {
      clean,
      startIndex = 0,
      stopIndex = DEFAULT_PAGING_LENGTH,
      columns,
      sortedColumn,
      sortOrder,
      serverQuery,
    } = page;
    if (columns && (columns !== this.refColumns)) {
      this.encodedColumns = this.encodeColumns(columns);
      this.refColumns = columns;
    }
    if (clean) {
      this.initDraw = this.draw;
    }
    const sortIndex = this.refColumns.indexOf(sortedColumn);
    const selection = this.encodeServerQuery(serverQuery);
    const body = [
      `draw=${this.draw}`,
      this.encodedColumns,
      sortIndex >= 0 ? encodeURI(`order[0][column]=${sortIndex}`) : null,
      sortIndex >= 0 ? encodeURI(`order[0][dir]=${sortOrder}`) : null,
      encodeURI(`start=${startIndex}`),
      encodeURI(`length=${stopIndex - startIndex + 1}`),
      this.noSearch,
      selection && encodeURI(selection),
    ].filter(x => x).join('&');
    this.draw += 1;
    this.start += this.length;
    return {
      body,
      headers: ctx && ctx.etag ? { ...this.headers, etag: ctx.etag } : this.headers,
      method: 'POST',
    };
  }

  clear() {
    this.start = 0;
    this.initDraw = this.draw;
    return null;
  }
}

pagings.datatables = DataTablesPaging;

const registerPaging = (name, paging) => {
  pagings[name] = paging;
};

const createPaging = (name) => {
  if (!pagings[name]) {
    console.error('Unkwon paging ', name);
    return null;
  }
  const Clazz = pagings[name];
  return new Clazz();
};

const initializePaging = (paging) => {
  return typeof paging === 'string' ? createPaging(paging) : paging;
};

export { initializePaging as default, registerPaging };
